import React, { useState } from 'react';

interface FeedbackSectionProps {
  lastResult: { text: string; time: string };
  transcriptionLogs?: Array<{text: string, timestamp: string, isFinal: boolean, confidence: number}>;
}

const FeedbackSection: React.FC<FeedbackSectionProps> = ({ lastResult, transcriptionLogs = [] }) => {
  const [isDebugExpanded, setIsDebugExpanded] = useState(false);
  const hasResult = lastResult.text !== '-';
  const hasTranscriptionLogs = transcriptionLogs.length > 0;

  // Don't render anything if there's no result data and no transcription logs
  if (!hasResult && !hasTranscriptionLogs) {
    return null;
  }

  const isCorrect = lastResult.text.includes('✓');
  const isIncorrect = lastResult.text.includes('✗');

  return (
    <section className="bg-white/95 backdrop-blur-sm rounded-xl p-3 sm:p-4 shadow-lg border border-white/20">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
        <div data-testid="last-result" className="text-center p-2 sm:p-3 rounded-lg bg-slate-50/50 transition-colors">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 font-medium">
            Result
          </div>
          <div
            data-testid="result-text"
            className={`
              text-sm sm:text-base font-medium transition-colors leading-relaxed break-words
              ${isCorrect ? 'text-emerald-600' : isIncorrect ? 'text-red-600' : 'text-slate-400'}
            `}
          >
            {lastResult.text}
          </div>
        </div>

        <div data-testid="response-time" className="text-center p-2 sm:p-3 rounded-lg bg-slate-50/50 transition-colors">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 font-medium">
            Time
          </div>
          <div
            data-testid="time-text"
            className="text-sm sm:text-base font-medium text-indigo-600 leading-relaxed"
          >
            {lastResult.time}
          </div>
        </div>
      </div>

      {/* Debug Transcription Log Section */}
      {hasTranscriptionLogs && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <button
            onClick={() => setIsDebugExpanded(!isDebugExpanded)}
            className="flex items-center justify-between w-full text-left p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
            aria-expanded={isDebugExpanded}
          >
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                Debug Log
              </span>
              <span className="text-xs text-slate-400 bg-slate-200 px-2 py-1 rounded-full">
                {transcriptionLogs.length} entries
              </span>
            </div>
            <svg
              className={`w-4 h-4 text-slate-500 transition-transform ${isDebugExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isDebugExpanded && (
            <div className="mt-3 p-3 bg-slate-50 rounded-lg max-h-48 overflow-y-auto">
              <div className="space-y-1 text-xs font-mono">
                {transcriptionLogs.map((log, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-2 py-1 px-2 rounded ${
                      log.isFinal
                        ? 'bg-emerald-100 text-emerald-800 border-l-2 border-emerald-400'
                        : 'bg-blue-50 text-blue-700 border-l-2 border-blue-300'
                    }`}
                  >
                    <span className="text-slate-500 min-w-[60px] text-right">
                      {log.timestamp}
                    </span>
                    <span className="flex-1 font-medium">
                      "{log.text}"
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      log.isFinal ? 'bg-emerald-200 text-emerald-700' : 'bg-blue-200 text-blue-600'
                    }`}>
                      {log.isFinal ? 'FINAL' : 'INTERIM'}
                    </span>
                    <span className="text-slate-500 text-xs">
                      {Math.round(log.confidence * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default FeedbackSection;